// Auto-actualización de la app (2026-08-24, pedido de Irving: tablets con el
// build viejo en caché seguían operando semanas después de cada deploy).
//
// Cómo funciona: cada deploy publica `version.json` (generado en deploy.sh,
// servido con no-cache desde firebase.json). Este hook lo consulta cada 5 min
// y al volver la pestaña a ser visible; si la versión desplegada difiere de la
// embebida en el build (REACT_APP_VERSION), hay actualización pendiente y:
//
// - Al NAVEGAR entre pantallas se recarga directo (el estado de la pantalla
//   anterior ya se descartó — perder no pierde nada).
// - En pantallas "siempre encendidas" (tablero de cocina y listas realtime en
//   `/orders`) se recarga sola en cuanto sea SEGURO: sin diálogo abierto y sin
//   foco en un campo de texto (los mismos guards de la lección de la
//   Calculadora), reintentando cada 30 s.
// - En el resto (POS con carrito en memoria) NO se recarga sola: se muestra el
//   banner con botón (UpdateBanner) y la recarga llega en la siguiente
//   navegación — como cerrar caja termina la sesión, el peor caso es un turno.
//
// Anti-bucle: si ya nos recargamos por una versión y el desfase persiste (CDN
// atrasado, env mal armado), no volvemos a recargar solos por esa versión.
// La contraparte de krispis-web es más simple (recarga silenciosa); NO son
// réplicas de paridad — cada canal tiene sus reglas de cuándo es seguro.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RETRY_RELOAD_MS = 30 * 1000;
const RELOADED_FOR_KEY = 'krispis.updateReloadedFor';

// Tablero de cocina, lista de pedidos del CEDIS: consulta realtime sin captura
export const isAlwaysOnScreen = (pathname: string): boolean => /\/orders$/.test(pathname);

// ¿Es seguro recargar EN ESTE INSTANTE? Un diálogo abierto puede llevar una
// captura a medias (cobro, conteo, PIN) y un campo con foco es alguien tecleando
export const isSafeToReloadNow = (doc: Document): boolean => {
    if (doc.querySelector('.MuiDialog-root')) {
        return false;
    }
    const active = doc.activeElement as HTMLElement | null;
    if (
        active &&
        (['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName) ||
            active.isContentEditable ||
            active.getAttribute('contenteditable') === 'true')
    ) {
        return false;
    }
    return true;
};

export const fetchDeployedVersion = async (): Promise<string | null> => {
    try {
        const response = await fetch('/version.json', { cache: 'no-store' });
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        return typeof data?.version === 'string' && data.version ? data.version : null;
    } catch {
        // Sin conexión, o el dev server respondió index.html: no hay nada que hacer
        return null;
    }
};

export const useAppUpdateChecker = (options?: { alwaysAutoReload?: boolean }) => {
    const alwaysAutoReload = options?.alwaysAutoReload ?? false;
    const location = useLocation();
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const deployedVersionRef = useRef<string | null>(null);
    const previousPathnameRef = useRef(location.pathname);

    const reload = useCallback((manual: boolean) => {
        const deployed = deployedVersionRef.current;
        if (!deployed) {
            return;
        }
        if (!manual && localStorage.getItem(RELOADED_FOR_KEY) === deployed) {
            return;
        }
        localStorage.setItem(RELOADED_FOR_KEY, deployed);
        window.location.reload();
    }, []);

    const reloadNow = useCallback(() => reload(true), [reload]);

    // Consulta periódica + al volver la pestaña a ser visible (tablets dormidas)
    useEffect(() => {
        const currentVersion = process.env.REACT_APP_VERSION;
        if (!currentVersion) {
            return;
        }
        let cancelled = false;
        const check = async () => {
            const deployed = await fetchDeployedVersion();
            if (cancelled || !deployed) {
                return;
            }
            deployedVersionRef.current = deployed;
            setUpdateAvailable(deployed !== currentVersion);
        };
        // `void`: el chequeo de versión atiende sus propios errores y nadie lo
        // espera — si `/version.json` no responde, la app sigue como está.
        void check();
        const interval = setInterval(() => void check(), CHECK_INTERVAL_MS);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void check();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    // Con actualización pendiente: recarga sola donde aplica, en cuanto sea seguro
    useEffect(() => {
        if (!updateAvailable) {
            return;
        }
        const attempt = () => {
            if (
                (alwaysAutoReload || isAlwaysOnScreen(window.location.pathname)) &&
                isSafeToReloadNow(document)
            ) {
                reload(false);
            }
        };
        attempt();
        const interval = setInterval(attempt, RETRY_RELOAD_MS);
        return () => clearInterval(interval);
    }, [updateAvailable, alwaysAutoReload, reload]);

    // Al navegar entre pantallas, recarga directo: el estado anterior ya se descartó
    useEffect(() => {
        if (previousPathnameRef.current !== location.pathname) {
            previousPathnameRef.current = location.pathname;
            if (updateAvailable) {
                reload(false);
            }
        }
    }, [location.pathname, updateAvailable, reload]);

    return { updateAvailable, reloadNow };
};
